/// <amd-module name="Build/Admin/PoolsList/Components/PoolsList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";

import { Button } from "VSSUI/Button";
import { ILinkProps } from "VSSUI/Link";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Status, StatusSize, Statuses } from "VSSUI/Status";
import { ColumnMore, ITableColumn, ITableRow, SimpleTableCell, Table, TableCell, TableColumnStyle, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TaskAgentPool, TaskAgentPoolStatus, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { Owner } from "Build/Admin/Common/Components/Owner";
import { PoolIssueIcon } from "Build/Admin/Common/Components/PoolIssueIcon";
import { PoolTypeIcon } from "Build/Admin/Common/Components/PoolTypeIcon";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { isElastic, isHostedOrPoolProviderBacked } from "Build/Admin/Common/TaskAgentPoolUtils";

import { TaskAgentPoolWrapper, TaskAgentQueueWrapper } from "Build/Admin/Common/Client/Contracts/DistributedTask/Wrappers";
import * as Resources from "Build/Admin/PoolsList/Resources";
import { CheckIssue } from "Checks/Client/Contracts/PipelinesChecks";

export interface IPoolsListProps {
    agentQueues: TaskAgentQueue[];
    agentPools: TaskAgentPool[];
    agentPoolStatuses: { [key: string]: TaskAgentPoolStatus };
    agentQueueOrPoolLinkProvider(agentQueueOrPool: TaskAgentQueue | TaskAgentPool): string;
    onDeleteAgentQueueOrPool(agentQueueOrPool: TaskAgentQueue | TaskAgentPool): void;
    onQueuePoolMaintenance(agentPool: TaskAgentPool): void;
    pageContext: IVssPageContext;
}

class TableListItem {
    value: TaskAgentQueueWrapper | TaskAgentPoolWrapper;
    linkProps: ILinkProps;
}

export class PoolsList extends React.PureComponent<IPoolsListProps> {
    private _projectDataService: IProjectDataService;
    private readonly _columns: ITableColumn<TableListItem>[];

    constructor(props: IPoolsListProps) {
        super(props);

        this._projectDataService = this.props.pageContext.getService<IProjectDataService>("IProjectDataService");
        this._columns = this._buildColumns();
    }

    public render(): JSX.Element {
        const itemProvider = new ArrayItemProvider<TableListItem>(this._buildListItems());
        return (
            <Table<TableListItem>
                className="pipelines-pools-list"
                columns={this._columns}
                itemProvider={itemProvider}
                onActivate={this._onRowClicked}
            />
        );
    }

    private _buildListItems(): TableListItem[] {
        let items: TableListItem[] = [];

        if (this._projectDataService.hasProjectData() && this.props.agentQueues) {
            this.props.agentQueues.forEach(queue => {
                const linkProps: ILinkProps = { href: this.props.agentQueueOrPoolLinkProvider(queue) };
                items.push({ value: queue, linkProps });
            });
        }
        else if (this.props.agentPools) {
            this.props.agentPools.forEach(pool => {
                const linkProps = { href: this.props.agentQueueOrPoolLinkProvider(pool) };
                items.push({ value: pool, linkProps });
            });
        }

        return items;
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<TableListItem>): void => {
        if (row.data.linkProps && row.data.linkProps.href) {
            onClickFPS(this.props.pageContext, row.data.linkProps.href, true, event);
        }
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        const agentPool: TaskAgentPoolWrapper | undefined = this.props.agentPools.filter(pool => pool.id === this._getPoolId(tableItem.value))[0];

        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                line1={this._renderPrimaryNameRowText(tableItem.value)}
                line2={this._renderSecondaryNameRowText(agentPool)}
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderPoolTypeIcon(agentPool, className)
                }}
                trailingLine1IconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderPoolIssueIcon(tableItem.value.issue)
                }}
            />
        );
    }

    private _renderPoolTypeIcon(tableItemPool: TaskAgentPool | undefined, className?: string | undefined): JSX.Element {
        return <PoolTypeIcon isHosted={isHostedOrPoolProviderBacked(tableItemPool)} isElastic={isElastic(tableItemPool)} className={className} />;
    }

    private _renderPoolIssueIcon(issue?: CheckIssue): JSX.Element {
        if (issue) {
            return <PoolIssueIcon issue={issue} />;
        }
        return <></>;
    }

    private _renderPrimaryNameRowText(tableItem: TaskAgentQueueWrapper | TaskAgentPoolWrapper): JSX.Element {
        return (
            <div>
                <Tooltip overflowOnly={true}>
                    <span className="fontWeightSemiBold fontSizeM text-ellipsis">
                        {tableItem.name}
                    </span>
                </Tooltip>
            </div>
        );
    }

    private _renderSecondaryNameRowText(agentPool: TaskAgentPool | undefined): JSX.Element {
        return (
            <span className="fontSize secondary-text flex-row flex-baseline">
                {
                    agentPool &&
                    agentPool.owner &&
                    <Owner identity={agentPool.owner} hidePersona={true} />
                }
            </span>
        );
    }

    private _renderQueuedCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        const id: number = this._getPoolId(tableItem.value);
        const agentPoolStatus: TaskAgentPoolStatus | undefined = this.props.agentPoolStatuses[id];
        const waitingRequests: number = agentPoolStatus ? agentPoolStatus.queuedRequestCount : 0;
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {
                    waitingRequests > 0 &&
                    <Status {...Statuses.Queued} className="margin-right-8" size={StatusSize.m} />
                }
                <span className="fontSizeM">
                    {waitingRequests > 0 && waitingRequests.toString()}
                </span>
            </SimpleTableCell>
        );
    }

    private _renderRunningCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        const id: number = this._getPoolId(tableItem.value);
        const agentPoolStatus: TaskAgentPoolStatus | undefined = this.props.agentPoolStatuses[id];
        const runningRequests: number = agentPoolStatus ? agentPoolStatus.runningRequestCount + agentPoolStatus.assignedRequestCount : 0;
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {
                    runningRequests > 0 &&
                    <Status {...Statuses.Running} className="margin-right-8" size={StatusSize.m} />
                }
                <span className="fontSizeM">
                    {runningRequests > 0 && runningRequests.toString()}
                </span>
            </SimpleTableCell>
        );
    }

    private _renderDeleteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TableCell className="bolt-table-cell-side-action" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-column">
                    <Button
                        ariaLabel={Resources.Delete}
                        className="bolt-table-cell-content-reveal"
                        subtle={true}
                        excludeTabStop={true}
                        iconProps={{
                            iconName: "Delete",
                            className: "fontSizeML"
                        }}
                        onClick={e => {
                            this.props.onDeleteAgentQueueOrPool(tableItem.value);
                            e.preventDefault();
                        }}
                    />
                </div>
            </TableCell>
        );
    }

    private _buildColumns = (): ITableColumn<TableListItem>[] => {
        const columns: ITableColumn<TableListItem>[] = [
            {
                id: "name",
                name: Resources.Name,
                width: new ObservableValue(-100),
                columnStyle: TableColumnStyle.Primary,
                renderCell: this._renderNameCell
            } as ITableColumn<TableListItem>,
            {
                id: "queued",
                name: Resources.QueuedJobs,
                width: new ObservableValue(120),
                columnStyle: TableColumnStyle.Secondary,
                renderCell: this._renderQueuedCell
            } as ITableColumn<TableListItem>,
            {
                id: "running",
                name: Resources.RunningJobs,
                width: new ObservableValue(120),
                columnStyle: TableColumnStyle.Secondary,
                renderCell: this._renderRunningCell
            } as ITableColumn<TableListItem>,
        ];

        if (this._projectDataService.hasProjectData()) {
            columns.push({
                id: "delete",
                width: new ObservableValue(40),
                columnStyle: TableColumnStyle.Tertiary,
                renderCell: this._renderDeleteCell
            } as ITableColumn<TableListItem>);
        }
        else {
            columns.push(new ColumnMore<TableListItem>(this._getContextMenuItems));
        }

        return columns;
    }

    private _getContextMenuItems = (listItem: TableListItem): IMenuProps => {
        if ((listItem.value as TaskAgentPool).isHosted) {
            return {
                id: "pool-menu",
                items: []
            };
        }

        const items: IMenuItem[] = [
            {
                id: "queueMaintenance",
                text: Resources.QueueMaintenanceJob,
                onActivate: () => {
                    this.props.onQueuePoolMaintenance(listItem.value as TaskAgentPool);
                }
            } as IMenuItem,
            {
                id: "delete",
                text: Resources.Delete,
                onActivate: () => {
                    this.props.onDeleteAgentQueueOrPool(listItem.value);
                }
            } as IMenuItem
        ];

        return {
            id: "pool-menu",
            items
        };
    }

    private _getPoolId(poolOrQueue: TaskAgentPool | TaskAgentQueue): number {
        if (this._projectDataService.hasProjectData() && this.props.agentQueues) {
            const agentQueue: TaskAgentQueue = poolOrQueue as TaskAgentQueue;
            if (agentQueue.pool) {
                return agentQueue.pool.id;
            }
        }
        else if (this.props.agentPools) {
            const agentPool: TaskAgentPool = poolOrQueue as TaskAgentPool;
            return agentPool.id;
        }

        return 0;
    }
}
